// src/components/StatsSection.jsx
import React from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { useNavigate } from "react-router-dom";

// --- Stats Data (Tailored for Crack Developers) ---
const statsData = [
  {
    target: 15,
    suffix: "+",
    title: "Dedicated Experts",
    description: "Dedicated professionals delivering cutting-edge results.",
  },
  {
    target: 50,
    suffix: "+",
    title: "Projects Delivered",
    description:
      "Successfully delivered diverse projects for clients worldwide.",
  },
  {
    target: 100,
    suffix: "%",
    title: "Client Satisfaction Rate",
    description:
      "Long-term relationships built on trust, clarity, and results.",
  },
  {
    target: 30,
    suffix: "+",
    title: "Clients Served",
    description:
      "Supporting businesses of all sizes with scalable tech solutions.",
  },
];

// --- CountUp Component ---
const CountUp = ({ target, suffix, duration = 1.5 }) => {
  const motionValue = useMotionValue(0);
  const [count, setCount] = React.useState(0);

  const startCount = () => {
    motionValue.set(0);
    animate(motionValue, target, {
      duration,
      ease: "easeOut",
      onUpdate: (latest) => setCount(Math.floor(latest)),
    });
  };

  return (
    <motion.span
      onViewportEnter={startCount}
      viewport={{ once: true, amount: 0.5 }}
    >
      {count}
      {suffix}
    </motion.span>
  );
};

// --- Stat Card ---
const StatCard = ({ target, suffix, title, description }) => (
  <div className="p-6 md:p-8 flex flex-col justify-start">
    <h3 className="text-4xl sm:text-5xl font-light text-white mb-4">
      <span className="font-semibold text-6xl sm:text-7xl">
        <CountUp target={target} suffix={suffix} />
      </span>
    </h3>
    <h4 className="text-lg sm:text-xl font-medium text-white mb-2">{title}</h4>
    <p className="text-gray-300 text-sm sm:text-base">{description}</p>
  </div>
);

// --- Stats Section ---
const StatsSection = () => {
  const navigate = useNavigate();
  return (
    <section className="relative font-sans flex flex-col items-center">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://framerusercontent.com/images/YFksbu7NQQEuJDIE8SC9tMUKkQ.jpg"
          alt="Office background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl px-6 lg:px-8 pt-4 pb-20">
        {/* Header */}
        <div className="mb-12 text-center lg:text-left max-w-3xl mx-auto lg:mx-0">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white leading-snug">
            Crack Developers: <strong>Innovating services and products</strong>
            for the digital world.
          </h2>
          <p className="text-gray-300 mt-4 text-sm sm:text-base">
            We are a service & product-based tech company, delivering scalable
            software solutions and products that empower businesses and
            end-users.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsData.map((stat, index) => (
            <div
              key={index}
              className="backdrop-blur-md bg-black/20 rounded-lg transition-all duration-300 hover:bg-black/30"
            >
              <StatCard {...stat} />
            </div>
          ))}
        </div>
      </div>

      {/* Floating CTA Button */}
      <div className="absolute bottom-6 right-6 z-20">
        <button
          onClick={() => {
            navigate("/contact");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center space-x-3 px-5 py-3 border-white border-2 text-white text-sm sm:text-base font-medium rounded-lg hover:bg-white hover:text-black transition duration-300"
        >
          <span>Let's build your next product</span>
          <svg
            className="w-4 h-4 transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            ></path>
          </svg>
        </button>
      </div>
    </section>
  );
};

export default StatsSection;
